/**
 * デザイン付きチケットPDF生成
 * HTML/CSSテンプレートをPDFに変換
 */

import { getTemplate, ticketSizes } from './ticketTemplates';

/**
 * デザイン付きチケットPDFを生成
 * @param {Array} qrCodes - QRコード配列
 * @param {string} ticketName - チケット名
 * @param {string} templateId - テンプレートID ('modern', 'classic', 'pop')
 * @param {Object} options - オプション設定
 * @param {string} options.size - チケットサイズ ('standard', 'compact', 'business-card', 'full-page')
 * @returns {Promise<void>}
 */
export const generateDesignedTicketPDF = async (qrCodes, ticketName, templateId = 'modern', options = {}) => {
  if (typeof window === 'undefined') {
    throw new Error('This function must be called from PC/Web environment');
  }

  console.log('[PDF Generator] Starting...');
  console.log('[PDF Generator] QR codes:', qrCodes?.length);
  console.log('[PDF Generator] Template:', templateId);

  if (!Array.isArray(qrCodes) || qrCodes.length === 0) {
    throw new Error('PDF化するQRコードがありません');
  }

  try {
    // html2canvasとjsPDFを動的インポート
    console.log('[PDF Generator] Loading libraries...');
    const html2canvasModule = await import('html2canvas');
    const html2canvas = html2canvasModule.default || html2canvasModule;
    const { jsPDF } = await import('jspdf');
    console.log('[PDF Generator] Libraries loaded');

    const template = getTemplate(templateId);
    const ticketSize = ticketSizes[options.size || 'standard'] || ticketSizes.standard;
    
    // PDFサイズを設定
    const pdfOrientation = ticketSize.orientation || 'landscape';
    const pdfFormat = options.size === 'full-page' ? 'a4' : [
      parseFloat(ticketSize.width),
      parseFloat(ticketSize.height)
    ];
    
    const pdf = new jsPDF({
      orientation: pdfOrientation,
      unit: 'mm',
      format: pdfFormat,
      compress: true
    });

    let isFirstPage = true;

    for (let i = 0; i < qrCodes.length; i++) {
      const qr = qrCodes[i];
      
      console.log(`[PDF Generator] Processing QR ${i + 1}/${qrCodes.length}:`, {
        hasCode: !!qr?.code,
        hasImageData: !!qr?.imageData,
        imageDataLength: qr?.imageData?.length
      });
      
      if (!qr?.imageData || !qr?.code) {
        console.warn(`[PDF Generator] Skipping QR ${i + 1}: missing data`);
        continue;
      }

      // 新しいページを追加（最初のページ以外）
      if (!isFirstPage) {
        pdf.addPage();
      }
      isFirstPage = false;

      // チケットデータを準備
      const ticketData = {
        eventName: ticketName || 'イベント',
        eventDate: options.eventDate || '',
        ticketNumber: qr.code || String(i + 1).padStart(4, '0'), // QRコードのIDを使用
        holderName: options.holderName || '',
        seatInfo: options.seatInfo || '',
        freeText: qr.freeText || '',
        qrCodeDataUrl: qr.imageData
      };

      // HTMLテンプレートを生成（サイズ指定）
      const htmlContent = template(ticketData, options.size || 'standard');

      // 一時的なコンテナを作成
      const container = document.createElement('div');
      container.style.position = 'absolute';
      container.style.left = '-9999px';
      container.style.top = '-9999px';
      container.innerHTML = htmlContent;
      document.body.appendChild(container);

      try {
        console.log(`[PDF Generator] Rendering HTML to canvas for QR ${i + 1}...`);
        // HTMLをCanvasに変換
        const canvas = await html2canvas(container.firstElementChild, {
          scale: 2,
          useCORS: true,
          logging: false,
          backgroundColor: null
        });

        // CanvasをPDFに追加
        const imgData = canvas.toDataURL('image/png');
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = pdf.internal.pageSize.getHeight();
        
        pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight, undefined, 'FAST');
        console.log(`[PDF Generator] Added QR ${i + 1} to PDF`);

        // 進捗表示（オプション）
        if (options.onProgress && typeof options.onProgress === 'function') {
          options.onProgress(i + 1, qrCodes.length);
        }
      } finally {
        // 一時コンテナを削除
        document.body.removeChild(container);
      }
    }

    // PDFを保存
    const safeTicketName = String(ticketName || 'ticket').replace(/[\\/:*?"<>|]/g, '_');
    const fileName = `ticket_${safeTicketName}_${templateId}_${new Date().toISOString().split('T')[0]}.pdf`;
    console.log('[PDF Generator] Saving PDF:', fileName);
    pdf.save(fileName);
    console.log('[PDF Generator] PDF saved successfully');

  } catch (error) {
    console.error('[PDF Generator] Failed:', error);
    throw error;
  }
};

/**
 * デザインプレビューを生成
 * @param {string} templateId - テンプレートID
 * @param {string} ticketName - チケット名
 * @param {string} qrCodeDataUrl - QRコードのData URL
 * @param {string} size - チケットサイズ
 * @param {Object} options - オプション設定
 * @param {string} options.eventDate - イベント日時
 * @param {string} options.seatInfo - 座席情報
 * @param {boolean} options.isHtmlMode - HTMLモードかどうか
 * @returns {string} HTMLコンテンツ
 */
export const generateTemplatePreview = (templateId, ticketName, qrCodeDataUrl, size = 'standard', options = {}) => {
  const template = getTemplate(templateId);
  
  const ticketData = {
    eventName: ticketName || 'サンプルイベント',
    eventDate: options.eventDate || '2026年3月15日 18:00',
    ticketNumber: '0001',
    holderName: '',
    seatInfo: options.seatInfo || '',
    isHtmlMode: options.isHtmlMode || false,
    qrCodeDataUrl: qrCodeDataUrl || 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg=='
  };

  return template(ticketData, size);
};
